import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NavigationContextService } from '../../core/services/navigation-context.service';
import { FormDropdownService } from '../../core/services/form-dropdown.service';
import {
  ContractRequest,
  ContractResponse,
  ApiResponse,
  ContractDocumentsRequest,
  DocumentType,
} from '../models/contract.model';

export interface Contract {
  contractId: string;
  serviceType: string;
  contractObject: string;
  startDate: string | null;
  endDate: string | null;
  value: number | null;
  status: 'Attivi' | 'In scadenza' | 'Rinnovo in corso' | 'Scaduto' | 'Bozza';
  vrfUseCase: 'Regulatory Critical' | 'Business Important' | 'Non Critical';
}

@Injectable({
  providedIn: 'root',
})
export class ContractRegistryService {
  private http = inject(HttpClient);
  private navContext = inject(NavigationContextService);
  private formDropdownService = inject(FormDropdownService);
  private baseUrl = `${environment.backofficeApiUrl}/api/v1`;
  
  constructor() {
    // Removed preload from constructor - components that need it will call it explicitly
  }

  /**
   * Ottiene il parametro entityType in base al contesto di navigazione
   * @returns 'SUPPLIER' o 'PROFESSIONAL' (uppercase, singolare)
   */
  private getEntityTypeParam(): string {
    return this.navContext.isSupplierContext() ? 'SUPPLIER' : 'PROFESSIONAL';
  }

  /**
   * Ottiene la lista di tutti i contratti
   * @returns Observable con la lista di tutti i contratti
   */
  getAllContracts(): Observable<any> {
    return this.http.get(`${this.baseUrl}/contracts`, {
      params: { entityType: this.getEntityTypeParam() },
    });
  }

  /**
   * Ottiene la lista di tutti i contratti associati a un fornitore
   * @param supplierId ID del fornitore
   * @returns Observable con la lista dei contratti
   */
  getContractsBySupplier(supplierId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/contracts`, {
      // entityType fisso: con PROFESSIONAL il backend ignorerebbe supplierId restituendo tutti i contratti
      params: { supplierId, entityType: 'SUPPLIER' },
    });
  }

  /**
   * Ottiene la lista di tutti i contratti associati a un responsible
   * @param responsibleId ID del responsible
   * @returns Observable con la lista dei contratti
   */
  getContractsByResponsible(responsibleId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/contracts`, {
      // entityType omesso: con PROFESSIONAL/SUPPLIER il backend ignorerebbe responsibleId restituendo tutti i contratti
      params: { responsibleId },
    });
  }

  /**
   * Ottiene la lista di tutti i contratti associati a un sub-responsible
   * @param subResponsibleId ID del sub-responsible
   * @returns Observable con la lista dei contratti
   */
  getContractsBySubResponsible(subResponsibleId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/contracts`, {
      // entityType omesso: con PROFESSIONAL/SUPPLIER il backend ignorerebbe subResponsibleId restituendo tutti i contratti
      params: { subResponsibleId },
    });
  }

  /**
   * Ottiene la lista di tutti i contratti associati a un professional
   * @param professionalId ID del professional
   * @returns Observable con la lista dei contratti
   */
  getContractsByProfessional(professionalId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/contracts`, {
      params: { professionalId },
    });
  }

  /**
   * Ottiene i dettagli di un singolo contratto
   * @param contractId ID del contratto
   * @returns Observable con i dettagli del contratto
   */
  getContractById(contractId: string): Observable<ApiResponse<ContractResponse>> {
    return this.http.get<ApiResponse<ContractResponse>>(`${this.baseUrl}/contracts/${contractId}`);
  }

  /**
   * Elimina un contratto
   * @param contractId ID del contratto da eliminare
   * @returns Observable con l'esito dell'operazione
   */
  deleteContract(contractId: string): Observable<any> {
    return this.http.delete(`${this.baseUrl}/contracts/${contractId}`);
  }

  /**
   * Crea un nuovo contratto
   * @param contract Oggetto ContractRequest con i dati del contratto
   * @returns Observable con la risposta del backend (ApiResponse<string> contenente l'ID del contratto creato)
   */
  createContract(contract: ContractRequest): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/contracts`, contract);
  }

  /**
   * Aggiorna un contratto esistente
   * @param contractId ID del contratto da aggiornare
   * @param contract Oggetto ContractRequest con i dati aggiornati
   * @returns Observable con la risposta del backend (ContractResponse)
   */
  updateContract(contractId: string, contract: ContractRequest): Observable<ContractResponse> {
    return this.http.put<ContractResponse>(`${this.baseUrl}/contracts/${contractId}`, contract);
  }

  /**
   * Crea un nuovo Atto di Nomina per un contratto (Step 2 - prima volta)
   * @param contractId ID del contratto
   * @param attoDiNomina Dati dell'Atto di Nomina
   * @returns Observable con la risposta del backend
   */
  createAttoDiNomina(contractId: string, attoDiNomina: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/contracts/${contractId}/atto-di-nomina`,
      attoDiNomina,
    );
  }

  /**
   * Aggiorna un Atto di Nomina esistente (Step 2 - modifiche successive)
   * @param contractId ID del contratto
   * @param attoDiNomina Dati aggiornati dell'Atto di Nomina
   * @returns Observable con la risposta del backend
   */
  updateAttoDiNomina(contractId: string, attoDiNomina: any): Observable<any> {
    return this.http.put<any>(
      `${this.baseUrl}/contracts/${contractId}/atto-di-nomina`,
      attoDiNomina,
    );
  }

  /**
   * Elimina l'Atto di Nomina di un contratto
   * @param contractId ID del contratto
   * @returns Observable con la risposta del backend
   */
  deleteAttoDiNomina(contractId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/contracts/${contractId}/atto-di-nomina`);
  }

  /**
   * @deprecated Use createAttoDiNomina() or updateAttoDiNomina() instead
   * Salva o aggiorna l'Atto di Nomina di un contratto (Step 2)
   * @param contractId ID del contratto
   * @param attoDiNomina Dati dell'Atto di Nomina
   * @returns Observable con la risposta del backend
   */
  saveAttoDiNomina(contractId: string, attoDiNomina: any): Observable<any> {
    return this.http.post<any>(
      `${this.baseUrl}/contracts/${contractId}/atto-di-nomina`,
      attoDiNomina,
    );
  }

  /**
   * Recupera l'Atto di Nomina per un contratto
   * @param contractId ID del contratto
   * @returns Observable con i dati dell'Atto di Nomina
   */
  getAttoDiNomina(contractId: string): Observable<any> {
    return this.http.get<any>(`${this.baseUrl}/contracts/${contractId}/atto-di-nomina`);
  }

  /**
   * Aggiorna i template e le note dei documenti di un contratto (Step 3)
   * @param contractId ID del contratto
   * @param documentsData Dati dei template e note
   * @returns Observable con la risposta del backend
   */
  updateContractDocuments(
    contractId: string,
    documentsData: {
      contractTemplateId: string | null;
      attoDiNominaTemplateId: string | null;
      contractDocumentsNote: string | null;
    },
  ): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/contracts/${contractId}/documents`, documentsData);
  }

  /**
   * Attiva un contratto (cambia status da GENERATED ad ACTIVE)
   * @param contractId ID del contratto da attivare
   * @returns Observable con la risposta del backend
   */
  activateContract(contractId: string): Observable<any> {
    return this.http.patch<any>(`${this.baseUrl}/contracts/${contractId}/status`, {
      status: 'ACTIVE',
    });
  }

  /**
   * Scarica i documenti associati a un contratto
   * @param contractId ID del contratto
   * @returns Observable con il file da scaricare
   */
  downloadContractDocuments(contractId: string): Observable<Blob> {
    // TODO: Sostituire con vera chiamata API
    // return this.http.get(`${this.baseUrl}/contracts/${contractId}/documents`, { responseType: 'blob' });
    return of(new Blob(['Mock document content'], { type: 'application/pdf' }));
  }

  // =========================
  // UTILITY - Formatting and mapping methods
  // =========================

  private getProfessionalTypeCode(contract: any): string {
    const rawType = contract.professionalType ||
      contract.professional?.type ||
      contract.professional?.category ||
      contract.professionalCategory ||
      '';
    const normalizedType = String(rawType).toUpperCase().trim();
    const aliases: Record<string, string> = {
      BODY_SHOP: 'BODYSHOP',
      CARROZZERIA: 'BODYSHOP',
      EXPERT_RE: 'RE_EXPERT',
      'PERITO RE': 'RE_EXPERT',
      'PERITO AUTO': 'AUTO_EXPERT',
      MEDICAL_EXPERT: 'DOCTOR',
      MEDICO: 'DOCTOR',
      INVESTIGATORE: 'INVESTIGATOR',
      LEGALE: 'LAWYER',
    };

    return aliases[normalizedType] || rawType;
  }

  /**
   * Mappa i contratti per la visualizzazione nella tabella
   * Centralizza la logica di mappatura per evitare duplicazioni
   * @param contracts Array di contratti raw dal backend
   * @param vrfUseCaseOptions Opzioni dropdown per VRF Use Case (optional)
   * @param serviceTypeOptions Opzioni dropdown per Service Type (optional)
   */
  mapContractsForDisplay(
    contracts: any[],
    vrfUseCaseOptions?: any[],
    serviceTypeOptions?: any[],
  ): any[] {
    return contracts.map((contract: any) => {
      const mapped = {
        ...contract,
        // ID contratto: usa sempre contract.id (contractId del contratto)
        contractId: contract.id || contract.contractId || '',
        serviceType: this.getDropdownDisplay(contract.serviceType, serviceTypeOptions || []),
        startDate: contract.startDate ? this.formatDate(contract.startDate) : '',
        endDate: contract.endDate ? this.formatDate(contract.endDate) : '',
        value: contract.value != null ? this.formatCurrency(contract.value) : '',
        statusDisplay: this.getStatusDisplay(contract.status),
        vrfUseCase: this.getDropdownDisplay(contract.vrfUseCase, vrfUseCaseOptions || []),
        // Professional-specific fields (when available)
        // Coalescenza: businessName oppure "Cognome Nome"
        professionalBusinessName: contract.businessName ||
          (contract.lastname && contract.firstname ? `${contract.lastname} ${contract.firstname}` : '') ||
          contract.professional?.businessName || 
          contract.professionalBusinessName || '',
        // Ragione Sociale fornitore
        supplierName: contract.supplierName || contract.supplier?.supplierName || contract.supplier?.companyName || '',
        professionalVatNumber: contract.vatNumber || contract.professional?.vatNumber || contract.professionalVatNumber || '',
        professionalCategory: this.getProfessionalTypeCode(contract),
        buyer: contract.buyerIpName || contract.buyer || contract.administration?.buyer || '',
        // Oggetto contratto: versione intera per tooltip + troncata per colonna 'truncated'
        contractObject_full: contract.contractSubject || contract.contractObject || contract.generalInfo?.contractSubject || '',
        contractObject: (() => {
          const full = contract.contractSubject || contract.contractObject || contract.generalInfo?.contractSubject || '';
          return full.length > 60 ? full.substring(0, 60) + '…' : full;
        })(),
      };
      
      // Log per debug (rimuovere in produzione)
      if (contract.professionalId || contract.generalInfo?.professionalId) {
        console.log('🔧 Mapping professional contract:', {
          original: contract,
          mapped: mapped,
          professionalFields: {
            businessName: mapped.professionalBusinessName,
            vatNumber: mapped.professionalVatNumber,
            category: mapped.professionalCategory,
            buyer: mapped.buyer,
            contractObject: mapped.contractObject
          }
        });
      }
      
      return mapped;
    });
  }

  parseContractDate(date: Date | string | null | undefined): Date | null {
    if (!date) return null;
    if (date instanceof Date) return date;

    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }

    return new Date(date);
  }

  formatContractDateForApi(date: Date | string | null | undefined): string | null {
    const parsedDate = this.parseContractDate(date);
    if (!parsedDate || Number.isNaN(parsedDate.getTime())) return null;

    const year = String(parsedDate.getFullYear()).padStart(4, '0');
    const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
    const day = String(parsedDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Formatta una data ISO in formato italiano dd/mm/yyyy
   */
  formatDate(dateString: string): string {
    try {
      const date = this.parseContractDate(dateString);
      if (!date) return dateString;
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }

  /**
   * Formatta un numero come valuta in formato italiano (separatore migliaia . e decimali ,)
   */
  formatCurrency(value: number): string {
    try {
      return new Intl.NumberFormat('it-IT', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    } catch (error) {
      console.error('Error formatting currency:', error);
      return value.toString();
    }
  }

  /**
   * Converte lo status del contratto in formato leggibile
   */
  getStatusDisplay(status: string): string {
    switch (status) {
      case 'DRAFT':
      case 'GENERATED':
        return 'BOZZA';
      case 'ACTIVE':
        return 'ATTIVO';
      case 'IN_RENEWAL':
        return 'IN RINNOVO';
      case 'EXPIRED':
        return 'SCADUTO';
      case 'CEASED':
        return 'CESSATO';
      default:
        return status;
    }
  }

  /**
   * Ritorna la severity PrimeNG per un dato status (per tag e badge)
   */
  getStatusSeverity(
    status: string,
  ): 'success' | 'secondary' | 'info' | 'warn' | 'danger' | 'contrast' | undefined {
    switch (status) {
      case 'DRAFT':
      case 'GENERATED':
        return 'secondary';
      case 'ACTIVE':
        return 'success';
      case 'IN_RENEWAL':
        return 'warn';
      case 'EXPIRED':
        return 'danger';
      case 'CEASED':
        return 'danger';
      default:
        return 'secondary';
    }
  }

  /**
   * Helper per ottenere il valore display da una lista di dropdown options
   */
  private getDropdownDisplay(value: string, options: any[]): string {
    const option = options.find((opt) => opt.valueKey === value);
    return option?.valueDescription || value;
  }

  /**
   * Upload Main Document - Carica il documento principale del contratto
   * @param contractId ID del contratto
   * @param file File da caricare
   * @param uploadedBy Chi ha caricato il file (default: 'system')
   * @param documentType Tipo di documento: UNSIGNED | EXTERNALLY_SIGNED | FINAL (default: FINAL)
   * @returns Observable con la risposta contenente l'URL S3
   */
  uploadMainDocument(
    contractId: string,
    file: File,
    uploadedBy: string = 'system',
    documentType: DocumentType = DocumentType.FINAL,
  ): Observable<{
    status: string;
    message: string;
    data: string; // S3 URL
  }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('uploadedBy', uploadedBy);

    return this.http.post<{
      status: string;
      message: string;
      data: string;
    }>(`${this.baseUrl}/contracts/${contractId}/main-document?documentType=${documentType}`, formData);
  }

  /**
   * Upload Countersigned Document - Carica il documento controfirmato (professional workflow)
   * Utilizza il documentType EXTERNALLY_SIGNED per distinguere dalla firma interna
   * @param contractId ID del contratto
   * @param file File controfirmato da caricare
   * @param uploadedBy Chi ha caricato il documento (default: 'system')
   * @returns Observable con la risposta contenente l'URL S3
   */
  uploadCountersignedDocument(
    contractId: string,
    file: File,
    uploadedBy: string = 'system',
  ): Observable<{
    status: string;
    message: string;
    data: string; // S3 URL
  }> {
    // Usa EXTERNALLY_SIGNED per il documento controfirmato dal fiduciario
    return this.uploadMainDocument(contractId, file, uploadedBy, DocumentType.EXTERNALLY_SIGNED);
  }

  /**
   * Upload Contract Attachment - Carica un allegato del contratto
   * @param contractId ID del contratto
   * @param file File da caricare
   * @param title Titolo/descrizione del documento
   * @returns Observable con la risposta contenente i dati del documento caricato
   */
  uploadContractAttachment(
    contractId: string,
    file: File,
    title: string,
  ): Observable<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('description', title);

    return this.http.post<ApiResponse<any>>(
      `${this.baseUrl}/contracts/${contractId}/attachments`,
      formData,
    );
  }

  /**
   * Get Attachments - Recupera la lista degli allegati di un contratto
   * @param contractId ID del contratto
   * @returns Observable con la lista degli allegati
   */
  getAttachments(contractId: string): Observable<ApiResponse<any[]>> {
    return this.http.get<ApiResponse<any[]>>(`${this.baseUrl}/contracts/${contractId}/attachments`);
  }

  /**
   * Delete Attachment - Elimina un allegato del contratto
   * @param contractId ID del contratto
   * @param documentId ID del documento da eliminare
   * @returns Observable con la risposta di eliminazione
   */
  deleteAttachment(contractId: string, documentId: string): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(
      `${this.baseUrl}/contracts/${contractId}/attachments/${documentId}`,
    );
  }

  /**
   * Download Attachment - Scarica un allegato del contratto
   * @param contractId ID del contratto
   * @param documentId ID del documento da scaricare
   * @returns Observable con il Blob del file
   */
  downloadAttachment(contractId: string, documentId: string): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/contracts/${contractId}/attachments/${documentId}`, {
      responseType: 'blob',
    });
  }

  /**
   * Download Main Document - Scarica il documento principale del contratto
   * @param contractId ID del contratto
   * @param documentType Tipo di documento: UNSIGNED | EXTERNALLY_SIGNED | FINAL (default: FINAL)
   * @returns Observable con il Blob del file
   */
  downloadMainDocument(contractId: string, documentType: DocumentType = DocumentType.FINAL): Observable<Blob> {
    return this.http.get(`${this.baseUrl}/contracts/${contractId}/main-document?documentType=${documentType}`, {
      responseType: 'blob',
    });
  }

  /**
   * Delete Main Document - Elimina il documento principale del contratto
   * @param contractId ID del contratto
   * @param documentType Tipo di documento: UNSIGNED | EXTERNALLY_SIGNED | FINAL (default: FINAL)
   * @returns Observable con la risposta di eliminazione
   */
  deleteMainDocument(contractId: string, documentType: DocumentType = DocumentType.FINAL): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/contracts/${contractId}/main-document?documentType=${documentType}`);
  }

  /**
   * Update Administration - Aggiorna la sezione amministrazione di un contratto
   * Utilizzabile anche per contratti in stato ACTIVE
   * @param contractId ID del contratto
   * @param administration Dati della sezione amministrazione
   * @returns Observable con la risposta del backend
   */
  updateAdministration(
    contractId: string,
    administration: {
      managingArea: string | null;
      claimsAreaReference: string | null;
      eventualReferent: string | null;
      buyer: string | null;
      involvedStructures: string[];
      invoiceRecipientEmail: string | null;
      documentationAtIP: boolean;
      supplierSignatory: string | null;
      supplierSignatoryRole: string | null;
      controlsOwner: string | null;
      paymentAuthorizer: string | null;
    },
  ): Observable<ApiResponse<null>> {
    return this.http.patch<ApiResponse<null>>(
      `${this.baseUrl}/contracts/${contractId}/administration`,
      administration,
    );
  }
}
